import { getPublicBranding } from '@hopin-team/api-hopin';
import { ThemeContext, ThemeProvider } from '@hopin-team/provider-theme';
import { rcTheme, Symbols } from '@hopin-team/ui-symbols';
import { useQuery } from '@tanstack/react-query';
import merge from 'lodash.merge';
import React, { useContext, useEffect, useState } from 'react';

import { client, LONG_QUERY_STALE_TIME } from '../..';
import { Loading } from '..';

const QUERY_KEY = ['branding'];

/** Disclaimer:
 * When this component was written, the theming in Hopin was not adequately set up to support slices.
 * Before copying this code, check with Fernando Pasik (@fernandopasik) to see if Design Systems have
 * given a cleaner approach.
 *
 * This component mimics the behavior of hopin-web -
 * - Loads the branding data from the backend
 * - Dumps the CSS vars into the dom
 * - Overrides the default hopin theme with the new colours
 */
type Theme = Record<string, string>;

export interface ThemeLoaderProps {
  eventSlug: string;
  enableCustomCSS?: boolean;
  enableBranding?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
  includesGlobalStyles?: boolean;
}

/**
 *
 * The advanced settings of custom branding lets users override specific schedules colors.
 * This is done exclusively through CSS variables, so we have to support it.
 */
export const defaultSchedulesCSSVars = (
  symbols: Symbols,
  theme: Theme | null,
) => ({
  'color-happening-now-card-background': `var(--color-gray-900, ${symbols.COLOR.GREY[900]})`,
  'color-happening-now-card-text': `var(--color-gray-300, ${symbols.COLOR.GREY[300]})`,
  'color-card-background': `var(--color-white, ${symbols.COLOR.GREY.WHITE})`,
  'color-schedule-background': 'var(--color-card-background)',
  'color-schedule-title': `var(--color-gray-800, ${symbols.COLOR.GREY[800]})`,
  'color-schedule-description': `var(--color-gray-700, ${symbols.COLOR.GREY[700]})`,
  'color-schedule-segment': `var(--color-gray-600, ${symbols.COLOR.GREY[600]})`,
  'color-schedule-highlight-background': `var(--color-primary-100, ${
    theme?.colorPrimary100 || symbols.COLOR.BLUE[100]
  })`,
  'color-schedule-highlight-title': `var(--color-gray-800, ${symbols.COLOR.GREY[800]})`,
  'color-schedule-highlight-description': `var(--color-gray-700, ${symbols.COLOR.GREY[700]})`,
  'color-schedule-highlight-segment': `var(--color-gray-700, ${symbols.COLOR.GREY[700]})`,
  'color-button-primary-background': `var(--color-primary-400, ${
    theme?.colorPrimary400 || symbols.COLOR.BLUE[500]
  })`,
  'color-button-primary-text': `var(--color-white, ${symbols.COLOR.GREY.WHITE})`,
});

const setCSSVar = (name: string, value: string) => {
  document.documentElement.style.setProperty(`--${name}`, value);
};

const setCustomCSSVars = (theme: Theme): void => {
  Object.keys(theme).forEach(key => {
    if (theme[key] !== '') {
      const themeVar = key
        .replace(/([a-z0-9])([A-Z]|[1-9]\d\d)/g, '$1-$2')
        .toLowerCase();

      setCSSVar(themeVar, theme[key]);
    }
  });
};

// the setting of these vars is mostly for local development. In production they are likely already set by the consumer.
const setScheduleCSSVars = (symbols: Symbols, theme: Theme | null): void => {
  Object.entries(defaultSchedulesCSSVars(symbols, theme)).forEach(
    ([key, value]) => {
      const isUnset =
        getComputedStyle(document.documentElement).getPropertyValue(
          `--${key}`,
        ) === '';

      if (value && isUnset) {
        setCSSVar(key, value);
      }
    },
  );
};

const parseThemeString = (theme: string) => {
  try {
    return JSON.parse(theme);
  } catch (_e) {
    return {};
  }
};

export const getThemeWithBrandColour = (
  brandColour: string,
  currentTheme: Symbols,
  theme?: any | null,
) => {
  const colour = brandColour || currentTheme.COLOR.BLUE.DEFAULT;
  const colourBranding = {
    COLOR: {
      THEME: {
        DEFAULT: colour,
        PRIMARY: colour,
        SECONDARY: colour,
        TERTIARY: colour,
      },
      GREY: {
        100: theme?.colorGray100 || currentTheme.COLOR.GREY[100],
        200: theme?.colorGray200 || currentTheme.COLOR.GREY[200],
        300: theme?.colorGray300 || currentTheme.COLOR.GREY[300],
        400: theme?.colorGray400 || currentTheme.COLOR.GREY[400],
        500: theme?.colorGray500 || currentTheme.COLOR.GREY[500],
        600: theme?.colorGray600 || currentTheme.COLOR.GREY[600],
        700: theme?.colorGray700 || currentTheme.COLOR.GREY[700],
        800: theme?.colorGray800 || currentTheme.COLOR.GREY[800],
        900: theme?.colorGray900 || currentTheme.COLOR.GREY[900],
        WHITE: theme?.colorWhite || currentTheme.COLOR.GREY.WHITE,
        BLACK: theme?.colorWhite || currentTheme.COLOR.GREY.BLACK,
        LIGHT: theme?.colorGray300 || currentTheme.COLOR.GREY.LIGHT,
        MEDIUM: theme?.colorGray650 || currentTheme.COLOR.GREY.MEDIUM,
        DARK: theme?.colorGray800 || currentTheme.COLOR.GREY.DARK,
      },
      UTILITY: {
        ACTION: colour,
        DEFAULT: colour,
      },
      BLUE: {
        100: 'var(--color-primary-100)',
        200: 'var(--color-primary-200)',
        300: 'var(--color-primary-300)',
        400: 'var(--color-primary-400)',
        500: 'var(--color-primary-500)',
        // This is a sketchy mapping
        600: 'var(--color-primary-500)',
        700: 'var(--color-primary-500)',
      },
    },
    LINE: {
      ACCENT_BORDER: `0.125rem solid ${colour}`,
      ACTIVE_BORDER: `${currentTheme.LINE.WIDTH} solid ${colour}`,
      SELECTED_BORDER: `${currentTheme.LINE.WIDTH} solid ${
        colour || currentTheme.COLOR.BLUE[300]
      }`,
    },
  };
  return merge(rcTheme, colourBranding);
};

export const CustomBranding = ({
  eventSlug,
  enableCustomCSS = true,
  enableBranding = true,
  fallback = <Loading id="theme-loading" />,
  children,
}: ThemeLoaderProps) => {
  const [themeApplied, setThemeApplied] = useState(!enableBranding);
  const { currentTheme, setCustomTheme } = useContext(ThemeContext);

  const { data, isFetched } = useQuery(
    QUERY_KEY,
    () =>
      getPublicBranding({
        client,
        eventSlug,
      }),
    {
      staleTime: LONG_QUERY_STALE_TIME,
      enabled: !!eventSlug && enableBranding,
    },
  );

  useEffect(() => {
    if (isFetched && data && !themeApplied) {
      const theme = parseThemeString(data.theme);

      if (data.color) {
        const colour = /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(data.color)
          ? `#${data.color}`
          : data.color;

        // Override colours inside the styled components theme
        const rcThemeWithColourBranding = getThemeWithBrandColour(
          colour,
          currentTheme,
          theme,
        );
        setCustomTheme(rcThemeWithColourBranding);
        setScheduleCSSVars(rcThemeWithColourBranding, theme);

        // Inject CSS vars and custom CSS into the DOM
        if (colour && theme) {
          setCustomCSSVars(theme);
        }

        if (enableCustomCSS && theme.customCSS) {
          const styleSheet = document.createElement('style');
          styleSheet.appendChild(document.createTextNode(theme.customCSS));
          document.body.prepend(styleSheet);
        }
      } else {
        setScheduleCSSVars(currentTheme, theme);
      }

      setThemeApplied(true);
    }

    if (!enableBranding) {
      setScheduleCSSVars(currentTheme, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  return themeApplied ? <>{children}</> : <>{fallback}</>;
};

export const ThemeLoader = ({
  includesGlobalStyles,
  ...props
}: ThemeLoaderProps) => {
  return (
    <ThemeProvider includesGlobalStyles={includesGlobalStyles}>
      <CustomBranding {...props} />
    </ThemeProvider>
  );
};
